import {
  useUpdateChapterStudentProgress,
  useUpdateHomeworkStudentProgress,
} from "hooks/api/student-progress";
import { useRole } from "hooks/common";
import { Fragment } from "react";
import { Space } from "components/Common/Space";
import { ExerciseHeader } from "components/Common/ExerciseHeader";
import { Divider } from "components/Common/Divider";
import { useParams } from "react-router-dom";
import { DndContainer } from "./DndContainer";
import {
  TMakeWordFromLettersExercise,
  TMakeWordFromLettersExercisePayload,
} from "types/app/exercises";
import "./MakeWordFromLetters.styles.scss";

type TMakeWordFromLettersProps = {
  isHomeworkExercise: boolean;
  /**
   * Chapter ID to which the exercise belongs.
   */
  chapterId: number;
  /**
   * An exercise raw data from API response.
   */
  exercise: TMakeWordFromLettersExercise;
  /**
   * Lesson chapter order number with the exercise sequence number to display before the exercise condition.
   * @default null
   */
  prefix?: string | null;
};

export const MakeWordFromLetters: React.FC<TMakeWordFromLettersProps> = (
  props
) => {
  const { chapterId, exercise, prefix = null, isHomeworkExercise } = props;

  const { courseId, lessonId, studentId } = useParams();
  const { isUserStudent } = useRole();

  const { mutate: updateChapterProgress } = useUpdateChapterStudentProgress(
    courseId,
    lessonId,
    chapterId,
    studentId
  );
  const { mutate: updateHomeworkProgress } = useUpdateHomeworkStudentProgress(
    courseId,
    lessonId,
    chapterId,
    studentId
  );

  const handleSaveExercise = (
    updatedExercise: TMakeWordFromLettersExercise
  ) => {
    if (!isUserStudent || !studentId) return;

    if (isHomeworkExercise) {
      updateHomeworkProgress(updatedExercise);
    } else {
      updateChapterProgress(updatedExercise);
    }
  };

  return (
    <Space direction="vertical" size="medium" justify="start" fullWidth>
      <ExerciseHeader<TMakeWordFromLettersExercisePayload>
        chapterId={chapterId}
        exercise={exercise}
        prefix={prefix}
      />
      <Space direction="vertical" size="medium" fullWidth>
        {exercise.payload.data.words.map((word, index) => (
          <Fragment key={word.id}>
            <DndContainer
              word={word}
              onSaveExercise={handleSaveExercise}
              exercise={exercise}
            />
            {index + 1 !== exercise.payload.data.words.length && (
              <Divider variant="dashed" />
            )}
          </Fragment>
        ))}
      </Space>
    </Space>
  );
};
